import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/lib/supabase";

// 발신 계정 잔액 조회용 토큰 (관리자 콘솔 전용)
const BALANCE_TOKEN = "mb-balance-9f3a7c2e";
// 이 금액 이하면 "부족" 경고
const LOW_BALANCE_THRESHOLD = 5;
// 자동 새로고침 주기 (ms)
const POLL_INTERVAL = 60_000;

export interface SenderBalanceData {
  checkedAt: string | null;
  activeKeySource: string | null;
  envKeyConfigured: boolean;
  httpStatus: number | null;
  amount: number | null;
  currency: string | null;
  balance: Record<string, unknown> | null;
  error: string | null;
}

export interface SenderBalanceState {
  data: SenderBalanceData | null;
  loading: boolean;
  error: string | null;
  amount: number | null;
  isCritical: boolean;
  isLow: boolean;
  refresh: () => void;
}

/**
 * 발신 계정(MessageBird) 잔액을 주기적으로 조회하는 훅.
 * 잔액 0 이하이면 isCritical, 임계값 이하이면 isLow 를 반환한다.
 */
export function useSenderBalance(): SenderBalanceState {
  const [data, setData] = useState<SenderBalanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchBalance = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res, error: fnError } = await supabase.functions.invoke("get-sender-balance", {
        body: { token: BALANCE_TOKEN },
      });
      if (fnError) throw new Error(fnError.message || "잔액 조회에 실패했습니다");
      if (!res) throw new Error("잔액 응답이 비어 있습니다");
      if (res.error) throw new Error(res.error);

      if (mountedRef.current) setData(res as SenderBalanceData);
    } catch (e) {
      if (mountedRef.current) {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    fetchBalance();

    const timer = setInterval(fetchBalance, POLL_INTERVAL);
    return () => {
      mountedRef.current = false;
      clearInterval(timer);
    };
  }, [fetchBalance]);

  const amount = data?.amount ?? null;
  const isCritical = amount !== null && amount <= 0;
  const isLow = amount !== null && amount > 0 && amount <= LOW_BALANCE_THRESHOLD;

  return { data, loading, error, amount, isCritical, isLow, refresh: fetchBalance };
}